/**
 * Menu Items Missing Recipes Page
 * Lists menu items that have no active inventory recipe, so staff can
 * follow up and create one — sourced from menu_items_missing_recipes_view.
 */

import { useMenuItemsMissingRecipes } from '@hooks/menu';
import { useAppSelector } from '@hooks/useRedux';
import { queryKeys } from '@lib/queryKeys';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutletId } from '@store/outlets';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Plus, RefreshCw, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { fetchAllMenuItemsMissingRecipesForExport } from '@/renderer/services/menuServicePaginated';
import { MenuItemMissingRecipe } from '@/renderer/types/menu';
import { apiError } from '@/renderer/util/notify';
import { Button, Input, OutletIndicator } from '@components/common';
import { PaginationControls } from '@components/common/PaginationControls';
import { useDebounce } from '../../../../hooks/useDebounce';
import { RecipeEditorModal } from './RecipeEditorModal';
import { buildSaveRecipePayload } from './recipeForm';
import { useRecipeEditorController } from './useRecipeEditorController';

const PAGE_SIZE = 20;

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-pos-primary/10 text-pos-primary',
  inactive: 'bg-gray-100 text-gray-600',
  out_of_stock: 'bg-amber-100 text-amber-700',
};

function StatusPill({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
        STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

export const MenuItemsMissingRecipesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const outletId = useAppSelector(selectEffectiveOutletId);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [isExporting, setIsExporting] = useState(false);

  const params = useMemo(
    () => ({ outletId, page, pageSize: PAGE_SIZE, search: debouncedSearch }),
    [outletId, page, debouncedSearch],
  );

  const { data, isLoading, isFetching, refetch } =
    useMenuItemsMissingRecipes(params);

  const { isModalOpen, openCreateModalForMenuItem, modalProps } =
    useRecipeEditorController(companyId, outletId ?? null);

  const rows: MenuItemMissingRecipe[] = data?.data ?? [];
  const isCompanyWide = !outletId;

  const handleRecipeSaved = async (
    payload: ReturnType<typeof buildSaveRecipePayload>,
  ) => {
    if (!modalProps) return;
    await modalProps.onSubmit(payload);
    queryClient.invalidateQueries({
      queryKey: queryKeys.menu.missingRecipes(params),
    });
  };

  const handleExportCSV = async () => {
    if (!outletId) {
      apiError('Select a specific outlet before exporting.');
      return;
    }
    setIsExporting(true);
    try {
      const result = await fetchAllMenuItemsMissingRecipesForExport(outletId);
      if (!result.success || !result.data) {
        apiError(result.error || 'Failed to fetch items for export.');
        return;
      }
      if (result.data.length === 0) {
        apiError('No items with missing recipes to export.');
        return;
      }

      const headers = [
        'Item',
        'Category',
        'Department',
        'Price',
        'Status',
        'SKU',
      ];
      const csvRows = result.data.map((item) =>
        [
          item.menu_item_name,
          item.category_name ?? '',
          item.department_name ?? '',
          item.base_price,
          item.menu_item_status,
          item.sku ?? '',
        ]
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(','),
      );
      const csvString = [headers.join(','), ...csvRows].join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `menu_items_missing_recipes_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'An error occurred during export.',
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <OutletIndicator description="Menu items with no active recipe. Select a specific outlet to filter, export, and create recipes for this list." />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Missing Recipes
          </h1>
          <p className="text-sm text-gray-500">
            {data?.pagination.totalItems ?? 0} menu item
            {data?.pagination.totalItems === 1 ? '' : 's'} with no active recipe
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={isExporting || !outletId}
            className="flex items-center gap-1.5 rounded-lg bg-pos-primary px-3 py-2 text-sm font-medium text-white hover:bg-pos-secondary disabled:opacity-60"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <Input
          placeholder="Search by item name"
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          className="pl-9"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="max-h-[32rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Item
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Category
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Department
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Price
                </th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Status
                </th>
                {isCompanyWide && (
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Outlet
                  </th>
                )}
                <th className="px-5 py-3" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={isCompanyWide ? 7 : 6}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={isCompanyWide ? 7 : 6}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    Every menu item has a recipe. Nothing to fix here.
                  </td>
                </tr>
              )}
              {rows.map((item) => (
                <tr
                  key={item.menu_item_id}
                  className="border-t border-gray-100"
                >
                  <td className="px-5 py-4 font-medium text-gray-900">
                    {item.menu_item_name}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {item.category_name ?? '—'}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {item.department_name ?? '—'}
                  </td>
                  <td className="px-5 py-4 text-right text-gray-900">
                    {item.base_price.toFixed(2)}
                  </td>
                  <td className="px-5 py-4">
                    <StatusPill status={item.menu_item_status} />
                  </td>
                  {isCompanyWide && (
                    <td className="px-5 py-4 text-gray-600">
                      {item.outlet_name}
                    </td>
                  )}
                  <td className="px-5 py-4">
                    <div className="flex justify-end">
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() =>
                          openCreateModalForMenuItem({
                            id: item.menu_item_id,
                            name: item.menu_item_name,
                          })
                        }
                      >
                        <Plus size={14} />
                        Create recipe
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data && (
        <PaginationControls
          pagination={data.pagination}
          itemLabel="items"
          onPageChange={setPage}
        />
      )}

      {isModalOpen && modalProps ? (
        <RecipeEditorModal {...modalProps} onSubmit={handleRecipeSaved} />
      ) : null}
    </div>
  );
};
